import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeader, handleUnauthorized } from '../helpers/api';
import './MetadataManagementModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const MetadataManagementModal = ({ isOpen, onClose, userId }) => {
  const [metadataList, setMetadataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingKind, setEditingKind] = useState('');
  const [newEntryName, setNewEntryName] = useState('');
  const [newEntryKind, setNewEntryKind] = useState('expense');
  const [error, setError] = useState(null);

  const loadMetadata = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const authHeader = getAuthHeader(userId);
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (kindFilter) params.append('kind', kindFilter);

      const res = await fetch(`${API_URL}/api/financial-entries-metadata?${params}`, {
        headers: {
          'Authorization': authHeader
        }
      });

      handleUnauthorized(res);
      if (!res.ok) {
        throw new Error(`Failed to load metadata: ${res.status}`);
      }

      const data = await res.json();
      setMetadataList(data.metadata || []);
    } catch (err) {
      console.error('Failed to load metadata:', err);
      setError('Erro ao carregar metadados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [userId, searchQuery, kindFilter]);

  useEffect(() => {
    if (isOpen) {
      loadMetadata();
    }
  }, [isOpen, loadMetadata]);

  const handleCreate = async () => {
    if (!newEntryName.trim()) return;

    try {
      const authHeader = getAuthHeader(userId);
      const res = await fetch(`${API_URL}/api/financial-entries-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          name: newEntryName,
          kind: newEntryKind
        })
      });

      handleUnauthorized(res);
      if (!res.ok) {
        throw new Error(`Failed to create metadata: ${res.status}`);
      }

      setNewEntryName('');
      setNewEntryKind('expense');
      loadMetadata();
    } catch (err) {
      console.error('Failed to create metadata:', err);
      setError('Erro ao criar entrada. Tente novamente.');
    }
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditingName(item.name);
    setEditingKind(item.kind);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingKind('');
  };

  const handleSaveEdit = async () => {
    if (!editingName.trim() || !editingId || !editingKind) return;

    try {
      const authHeader = getAuthHeader(userId);
      const res = await fetch(`${API_URL}/api/financial-entries-metadata/${editingId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          name: editingName,
          kind: editingKind
        })
      });

      handleUnauthorized(res);
      if (!res.ok) {
        throw new Error(`Failed to update metadata: ${res.status}`);
      }

      setEditingId(null);
      setEditingName('');
      setEditingKind('');
      loadMetadata();
    } catch (err) {
      console.error('Failed to update metadata:', err);
      setError('Erro ao atualizar entrada. Tente novamente.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;

    try {
      const authHeader = getAuthHeader(userId);
      const res = await fetch(`${API_URL}/api/financial-entries-metadata/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          archivedAt: true
        })
      });

      handleUnauthorized(res);
      if (!res.ok) {
        throw new Error(`Failed to delete metadata: ${res.status}`);
      }

      loadMetadata();
    } catch (err) {
      console.error('Failed to delete metadata:', err);
      setError('Erro ao excluir categoria. Tente novamente.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="metadata-modal-backdrop" onClick={onClose}>
      <div className="metadata-modal" onClick={e => e.stopPropagation()}>
        <div className="metadata-modal-header">
          <h2>Gerenciar Categorias</h2>
          <button className="metadata-close-btn" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="metadata-error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="metadata-create-section">
          <h3>Nova Categoria</h3>
          <div className="metadata-create-form">
            <input
              type="text"
              placeholder="Nome da categoria"
              value={newEntryName}
              onChange={(e) => setNewEntryName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreate()}
            />
            <select value={newEntryKind} onChange={(e) => setNewEntryKind(e.target.value)}>
              <option value="expense">Despesa</option>
              <option value="income">Receita</option>
            </select>
            <button onClick={handleCreate} disabled={!newEntryName.trim()}>
              Criar
            </button>
          </div>
        </div>

        <div className="metadata-filters">
          <input
            type="text"
            placeholder="Buscar categorias..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="metadata-search"
          />
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="metadata-kind-filter"
          >
            <option value="">Todos os tipos</option>
            <option value="expense">Despesas</option>
            <option value="income">Receitas</option>
          </select>
        </div>

        <div className="metadata-list-container">
          {loading ? (
            <div className="metadata-loading">Carregando...</div>
          ) : metadataList.length === 0 ? (
            <div className="metadata-empty">
              Nenhuma categoria encontrada.
              {(searchQuery || kindFilter) && ' Tente ajustar os filtros.'}
            </div>
          ) : (
            <table className="metadata-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Nome</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {metadataList.map((item) => (
                  <tr key={item.id}>
                    <td className="metadata-kind-cell">
                      {editingId === item.id ? (
                        <select
                          value={editingKind}
                          onChange={(e) => setEditingKind(e.target.value)}
                          className="metadata-kind-select"
                        >
                          <option value="expense">Despesa</option>
                          <option value="income">Receita</option>
                        </select>
                      ) : (
                        item.kind === 'income' ? (
                          <i className="fas fa-circle-plus" style={{ color: '#4caf50' }}></i>
                        ) : (
                          <i className="fas fa-circle-minus" style={{ color: '#f44336' }}></i>
                        )
                      )}
                    </td>
                    <td>
                      {editingId === item.id ? (
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                          autoFocus
                        />
                      ) : (
                        item.name
                      )}
                    </td>
                    <td className="metadata-date-cell">
                      {new Date(item.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="metadata-actions-cell">
                      {editingId === item.id ? (
                        <>
                          <button
                            className="metadata-action-btn save"
                            onClick={handleSaveEdit}
                            title="Salvar"
                          >
                            <i className="fas fa-check"></i>
                          </button>
                          <button
                            className="metadata-action-btn cancel"
                            onClick={handleCancelEdit}
                            title="Cancelar"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="metadata-action-btn edit"
                            onClick={() => handleStartEdit(item)}
                            title="Renomear"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="metadata-action-btn delete"
                            onClick={() => handleDelete(item.id)}
                            title="Excluir"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetadataManagementModal;
